/**
 * Canvas and image dimension constants for the engraving editor
 *
 * All images should be centered within the 1024x1536 canvas using
 * the centerOnCanvas() utility to ensure proper alignment.
 */

import type { Product } from '@/types/product.types';
import type { Orientation } from '@/types/orientation.types';

// Canvas dimensions (frame size) - all content is rendered within this space
export const CANVAS_WIDTH = 1024;
export const CANVAS_HEIGHT = 1536;

// Default AI-generated image dimensions (fallback when product has no specific size)
export const AI_IMAGE_WIDTH = 450;
export const AI_IMAGE_HEIGHT = 777;

/**
 * Get canvas dimensions for a specific product
 * Uses product-specific dimensions if defined, otherwise falls back to defaults (1024x1536)
 */
export function getCanvasSize(
  product: Product | null,
  orientation: Orientation = 'portrait'
): { width: number; height: number } {
  const portrait = {
    width: product?.canvasWidth ?? CANVAS_WIDTH,
    height: product?.canvasHeight ?? CANVAS_HEIGHT,
  };
  return orientation === 'landscape'
    ? { width: portrait.height, height: portrait.width }
    : portrait;
}

/**
 * Get AI image dimensions for a specific product
 * Uses product-specific dimensions if defined, otherwise falls back to defaults
 */
export function getAiImageSize(
  product: Product | null,
  orientation: Orientation = 'portrait'
): { width: number; height: number } {
  const portrait = {
    width: product?.aiImageWidth ?? AI_IMAGE_WIDTH,
    height: product?.aiImageHeight ?? AI_IMAGE_HEIGHT,
  };
  return orientation === 'landscape'
    ? { width: portrait.height, height: portrait.width }
    : portrait;
}

/**
 * Calculate centered position for an element on the canvas
 *
 * @param elementWidth - Width of the element to center
 * @param elementHeight - Height of the element to center
 * @param canvasW - Canvas width (defaults to CANVAS_WIDTH)
 * @param canvasH - Canvas height (defaults to CANVAS_HEIGHT)
 * @returns Object with x and y coordinates for centered position
 */
export function centerOnCanvas(
  elementWidth: number,
  elementHeight: number,
  canvasW: number = CANVAS_WIDTH,
  canvasH: number = CANVAS_HEIGHT
): { x: number; y: number } {
  return {
    x: (canvasW - elementWidth) / 2,
    y: (canvasH - elementHeight) / 2,
  };
}
